/**
 * Reads a PDF page's raw /MediaBox and /CropBox via pdf-lib.
 *
 * Why this exists: pdf.js never exposes the two boxes separately. Its worker
 * computes `view` as `intersect(cropBox, mediaBox)` (falling back to
 * `mediaBox` when both are equal) and the `GetPage` message only transports
 * `{ rotate, ref, refStr, userUnit, view }`. There is no worker handler that
 * returns the raw boxes, so the only way to obtain them on the main thread is
 * to read them back out of the original PDF bytes
 * (`PDFDocumentProxy.getData()`) and parse them ourselves. pdf-lib already
 * implements that parsing — including /Parent inheritance and the spec's
 * "CropBox defaults to MediaBox when absent" rule — so it is used here
 * instead of a bespoke PDF object reader.
 */

import { ParseSpeeds, PDFDocument } from 'pdf-lib';

export type PdfBox = [number, number, number, number];

export interface PdfPageBoxes {
  cropBox: PdfBox | null;
  mediaBox: PdfBox | null;
}

/** Shape of the pdf.js `PDFDocumentProxy` members this module relies on. */
interface PdfDocumentLike {
  getData: () => Promise<Uint8Array>;
}

function toBox(box: {
  x: number;
  y: number;
  width: number;
  height: number;
}): PdfBox {
  return [box.x, box.y, box.x + box.width, box.y + box.height];
}

const docsByPdfJsDocument = new WeakMap<object, Promise<PDFDocument | null>>();

function getPdfLibDocument(
  pdfDocument: PdfDocumentLike
): Promise<PDFDocument | null> {
  const cached = docsByPdfJsDocument.get(pdfDocument);
  if (cached) {
    return cached;
  }
  const pending = (async () => {
    try {
      const data = await pdfDocument.getData();
      if (!data?.length) {
        return null;
      }
      return await PDFDocument.load(data, {
        ignoreEncryption: true,
        updateMetadata: false,
        parseSpeed: ParseSpeeds.Fastest
      });
    } catch {
      return null;
    }
  })();
  docsByPdfJsDocument.set(pdfDocument, pending);
  return pending;
}

/**
 * Reads the raw /CropBox and /MediaBox of a single page.
 *
 * Returns `null` for either box when the document cannot be read, which lets
 * callers fall back to pdf.js' merged `view` rectangle.
 */
export async function getPdfPageBoxes(
  pdfDocument: PdfDocumentLike | null | undefined,
  pageNumber: number | null | undefined
): Promise<PdfPageBoxes> {
  const empty: PdfPageBoxes = { cropBox: null, mediaBox: null };
  if (
    typeof pdfDocument?.getData !== 'function' ||
    typeof pageNumber !== 'number' ||
    pageNumber < 1
  ) {
    return empty;
  }

  try {
    const pdfLibDocument = await getPdfLibDocument(pdfDocument);
    const page = pdfLibDocument?.getPages()[pageNumber - 1];
    if (!page) {
      return empty;
    }

    const mediaBox = toBox(page.getMediaBox());
    // Per the PDF spec /CropBox defaults to /MediaBox when absent, which
    // `getCropBox()` already accounts for.
    const cropBox = toBox(page.getCropBox());

    return { cropBox, mediaBox };
  } catch {
    return empty;
  }
}
